import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, BadgePercent, TrendingUp, ShieldAlert, ListChecks, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import EventCard from "../components/EventCard";

const VendorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({ credit_days: "", outstanding: "", contact: "" });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/vendors/${id}`);
      setData(r.data);
      setEdit({
        credit_days: r.data.vendor.credit_days ?? "",
        outstanding: r.data.vendor.outstanding ?? "",
        contact: r.data.vendor.contact ?? "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/vendors");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="vendor-detail-loading">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-500 dark:border-emerald-500/60 border-t-transparent animate-spin" />
      </div>
    );
  }

  const { vendor, events, open_tasks } = data;

  const save = async () => {
    try {
      await api.patch(`/vendors/${id}`, {
        credit_days: edit.credit_days !== "" ? parseInt(edit.credit_days, 10) : null,
        outstanding: edit.outstanding || null,
        contact: edit.contact || null,
      });
      toast.success("Brand profile saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirm = async (event) => {
    try {
      await api.post(`/events/${event.id}/confirm`, {});
      toast.success("Confirmed — vendor memory updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async (event) => {
    try {
      await api.post(`/events/${event.id}/reject`);
      toast.success("Event rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="vendor-detail-page" className="space-y-6">
      <button
        data-testid="vendor-back-btn"
        onClick={() => navigate("/vendors")}
        className="inline-flex items-center gap-1.5 text-base text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> All brands
      </button>

      <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-200 dark:bg-slate-700 border border-slate-300 dark:border-slate-700">
            <Building2 className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{vendor.name}</h1>
            <p className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">
              Autonomy: {vendor.autonomy === "auto" ? "Auto-classify earned" : "Human confirms first"}
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="vendor-edit-row">
          <div>
            <label className="block text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400 mb-1">Credit days</label>
            <input
              data-testid="vendor-edit-credit-days"
              type="number"
              value={edit.credit_days}
              onChange={(e) => setEdit({ ...edit, credit_days: e.target.value })}
              className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400 mb-1">Outstanding</label>
            <input
              data-testid="vendor-edit-outstanding"
              value={edit.outstanding}
              onChange={(e) => setEdit({ ...edit, outstanding: e.target.value })}
              placeholder="e.g. ₹1.2L"
              className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400 mb-1">Contact</label>
            <input
              data-testid="vendor-edit-contact"
              value={edit.contact}
              onChange={(e) => setEdit({ ...edit, contact: e.target.value })}
              className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
            />
          </div>
        </div>
        <button
          data-testid="vendor-save-profile-btn"
          onClick={save}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-slate-200 dark:bg-slate-700 px-3 py-2 text-sm font-semibold text-slate-800 dark:text-slate-200 hover:bg-slate-700 transition-colors"
        >
          <Save className="h-3.5 w-3.5" /> Save profile
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="vendor-memory-cards">
        <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
            <TrendingUp className="h-4 w-4" />
            <span className="text-sm font-mono uppercase tracking-widest">Last price revision</span>
          </div>
          {vendor.last_price_revision ? (
            <>
              <p className="text-base text-slate-800 dark:text-slate-200">{vendor.last_price_revision.title}</p>
              <p className="mt-1 text-sm font-mono text-slate-600 dark:text-slate-400">{new Date(vendor.last_price_revision.date).toLocaleDateString()}</p>
            </>
          ) : (
            <p className="text-base text-slate-600 dark:text-slate-400">None captured yet.</p>
          )}
        </div>
        <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 mb-2">
            <BadgePercent className="h-4 w-4" />
            <span className="text-sm font-mono uppercase tracking-widest">Current scheme</span>
          </div>
          {vendor.current_scheme ? (
            <>
              <p className="text-base text-slate-800 dark:text-slate-200">{vendor.current_scheme.title}</p>
              <p className="mt-1 text-sm font-mono text-cyan-600 dark:text-cyan-400">
                {vendor.current_scheme.expiry ? `expires ${vendor.current_scheme.expiry}` : "no expiry captured"}
              </p>
            </>
          ) : (
            <p className="text-base text-slate-600 dark:text-slate-400">No active scheme.</p>
          )}
        </div>
        <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
          <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 mb-2">
            <ShieldAlert className="h-4 w-4" />
            <span className="text-sm font-mono uppercase tracking-widest">Latest notice</span>
          </div>
          {vendor.last_notice ? (
            <>
              <p className="text-base text-slate-800 dark:text-slate-200">{vendor.last_notice.title}</p>
              <p className="mt-1 text-sm font-mono text-slate-600 dark:text-slate-400">{new Date(vendor.last_notice.date).toLocaleDateString()}</p>
            </>
          ) : (
            <p className="text-base text-slate-600 dark:text-slate-400">No notices.</p>
          )}
          {vendor.outstanding_note && <p className="mt-2 text-sm text-rose-700/80">{vendor.outstanding_note}</p>}
        </div>
      </div>

      {open_tasks.length > 0 && (
        <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4" data-testid="vendor-open-tasks">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="h-4 w-4 text-rose-600 dark:text-rose-400" />
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Open tasks</h2>
          </div>
          <ul className="space-y-1.5">
            {open_tasks.map((t) => (
              <li key={t.id} data-testid={`vendor-task-${t.id}`} className="text-base text-slate-700 dark:text-slate-300">
                • {t.title} <span className="text-sm font-mono text-slate-600 dark:text-slate-400">({t.event_title})</span>
                {t.due_date && <span className="ml-1 text-sm font-mono text-cyan-600 dark:text-cyan-400">{t.due_date}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div data-testid="vendor-timeline">
        <h2 className="text-lg md:text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3">History timeline</h2>
        {events.length === 0 ? (
          <p className="text-base text-slate-600 dark:text-slate-400">No events for this brand yet.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {events.map((e) => (
              <EventCard key={e.id} event={e} compact onConfirm={confirm} onReject={reject} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorDetail;
